import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Formik } from 'formik';

import * as Yup from 'yup';
import {
  ColorPalettes,
  Selecter,
  Grids,
  Breakpoints,
} from 'kinedu-react-components';
import { Button } from 'lego-dist';
import {
  DAY_OF_WEEK, DAYS_PER_WEEK, ACTIVITIES_PER_DAY, ASSESSMENTS_PER_CHILD,
} from '../constants/onBoarding';
import { AppEvents } from 'constants/events';
import EventReporter from 'appEvents';
import { KEYS_LOCALSTORAGE } from 'constants/general';
import useAccount from '../hooks/useAccount';

const { KineduPalette } = ColorPalettes;

const StyledPersonalizeView = styled.div`
${Grids.mobile}
  display: block;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;

  width: 100%;
  height: 100%;
  position: absolute;
  padding: 20px 35px;
  ${Breakpoints.desktop} {
    ${Grids.ob}
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    box-sizing: border-box;

    width: 100%;
    height: 100%;
    position: absolute;
    padding: 20px 55px;
    }
`;

const Wrapper = styled.div`
${Grids.mobile}
  display: flex;
  flex-direction: column;

  width: 100%;
  margin: 0 auto auto;

  .Selecter {

    max-width: 400px;
    /* margin: 0px auto 30px; */
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: stretch;

    .Select {
      margin: 0;
    }

    .Selecter-label {
      line-height: 24px;

      text-align: left;
      text-transform: unset;
      color: ${KineduPalette.shadeDark};
      font-weight: bold;
      letter-spacing: 0;
      font-size: 18px;
    }
  }

  .personalize-button {
    display: flex;
    align-self: center;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    margin: 18px auto;
  }

  ${Breakpoints.desktop} {
    ${Grids.ob}
    display: flex;
    flex-direction: row;

    width: 100%;
    margin: 0 auto auto;

    .Selecter {
      height: 140px;
      max-width: 300px;
      /* margin: 0px auto 30px; */
      margin: 10px 20px 30px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: stretch;

      .Select-option {
        text-transform: capitalize;
      }

      .Select {
        margin: 0;
      }

      .Selecter-label {
        line-height: 24px;

        text-align: left;
        text-transform: unset;
        color: ${KineduPalette.shadeDark};
        font-weight: bold;
        letter-spacing: 0;
        font-size: 18px;
      }
    }

    .personalize-button {
      display: flex;
      align-self: center;
      justify-content: center;
      align-items: center;
      flex-direction: column;

      margin: auto;
    }
    }
`;

const Row = styled.div`
${Grids.mobile}
  display: flex;
  flex-direction: column ;
  justify-content: flex-start;
  align-content: center;
  align-items: center;

  width: 100%;
  ${Breakpoints.desktop} {
    ${Grids.ob}
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-content: center;
    align-items: center;
    }
`;

const Title = styled.span`
${Grids.mobile}
  display: block;
  align-self: flex-start;
  margin: 0px 0px 20px;

  text-transform: unset;
  text-align: center;
  font-size: 28px;
  font-weight: bold;
  line-height: 47px;
  color: ${KineduPalette.newKinedu};
  ${Breakpoints.desktop} {
    ${Grids.ob}
    display: block;
    align-self: flex-start;
    margin: auto 30px 60px;

    text-transform: unset;
    text-align: left;
    font-size: 36px;
    font-weight: bold;
    line-height: 47px;
    }
`;

interface PersonalizeViewProps {
  nextStep: (values: {}) => {};
}

const PersonalizeView:React.SFC<PersonalizeViewProps> = ({ nextStep }) => {
  const { t } = useTranslation();
  const { user } = useAccount();
  const [mounted, setMounted] = useState<boolean>(false);


  useEffect(() => {
    if (user.id && !mounted) {
      localStorage.setItem(KEYS_LOCALSTORAGE.VIEW_HOW_IT_WORKS, 'true');
      EventReporter.view(AppEvents.S_OB_PERSONALIZED_PLAN());
      setMounted(true);
    }
  }, [user, mounted]);

  const translatedWeekdays = DAY_OF_WEEK.map((item) => ({ ...item, label: t(`${item.label}`) }));
  const translatedTimes = ASSESSMENTS_PER_CHILD.map((item) => ({ ...item, label: t(`ob:${item.label}`, { times: item.value }) }));

  return (
    <StyledPersonalizeView>
      <Title>{t('ob:PERSONALIZE_PLAN')}</Title>
      <Wrapper>
        <Formik
          initialValues={{
            dap_initial_day: '',
            plan_days: '',
            activities_per_day: '',
            assessmentsPerChild: '',
          }}
          validationSchema={Yup.object().shape({
            dap_initial_day: Yup.string().required(),
            plan_days: Yup.string().required(),
            activities_per_day: Yup.string().required(),
            assessmentsPerChild: Yup.string().required(),
          })}
          onSubmit={(values, actions) => {
            actions.setSubmitting(false);
            nextStep(values);
          }}
          render={({ handleSubmit, setFieldValue, isValid }) => (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Row>
                <Selecter
                  className="Selecter"
                  options={translatedWeekdays}
                  label={t('ob:Q1')}
                  onChange={(e: any) => setFieldValue('dap_initial_day', e.value)}
                />
                <Selecter
                  className="Selecter"
                  options={DAYS_PER_WEEK}
                  label={t('ob:Q2')}
                  onChange={(e: any) => setFieldValue('plan_days', e.value)}
                />
              </Row>
              <Row>
                <Selecter
                  className="Selecter"
                  options={ACTIVITIES_PER_DAY}
                  label={t('ob:Q3')}
                  onChange={(e: any) => setFieldValue('activities_per_day', e.value)}
                />
                <Selecter
                  className="Selecter"
                  options={translatedTimes}
                  label={t('ob:Q4')}
                  onChange={(e: any) => setFieldValue('assessmentsPerChild', e.value)}
                />
              </Row>
              <Button
                className="personalize-button"
                type="submit"
                fill
                maxWidth={300}
                flat
                rounded
                color="primary"
                disabled={!isValid}
              >
                {t('ob:FINISH')}
              </Button>
            </form>
          )}
        />
      </Wrapper>
    </StyledPersonalizeView>
  );
};

export default PersonalizeView;
